import "./SummaryTab.css";

import { Box } from "@saviynt/design-system";
import type { Dispatch, SetStateAction } from "react";
import { memo, useEffect } from "react";
import { useIntl } from "react-intl";

import {
  AccessEntitlements,
  AssetInformation,
  InsightsSummary,
  Relationships,
} from "./components";
import { useInventoryAssetSummaryData } from "../../../../hooks/usePolicy";
import LoadErrorWrapper from "../../../../components/LoadErrorWrapper/LoadErrorWrapper";
import Ownership from "./components/Ownership";
import { AssetMetadata } from "@repo/api/schemas/policiesInventory.schema";
import { VariantType } from "../InventorySidePanel";

interface SummaryTabProps {
  assetId: string;
  variant: VariantType;
  setAccountMetaData: Dispatch<SetStateAction<AssetMetadata | null>>;
}

function SummaryTab({ assetId, setAccountMetaData, variant }: SummaryTabProps) {
  const intl = useIntl();
  const { data, isLoading, isError } = useInventoryAssetSummaryData(assetId);

  const {
    application,
    assetInformation,
    relationships,
    accessEntitlements,
    assetMetadata = null,
    ownership,
  } = data?.data ?? {};
  const { isOpenToAllInternet = false } = assetInformation ?? {};

  useEffect(() => {
    if (!isLoading && !isError) {
      setAccountMetaData(assetMetadata);
    }
  }, [assetMetadata, setAccountMetaData, isLoading, isError]);

  if (isLoading || isError) {
    return (
      <Box className="w-full">
        <LoadErrorWrapper isLoading={isLoading} isError={isError} />
      </Box>
    );
  }

  return (
    <div
      className="Nhi-SummaryTab-Container"
      data-testid="NHI-SummaryTab-Content"
      role="region"
      aria-label={intl.formatMessage({
        defaultMessage: "Summary tab content",
        id: "oQy70I",
        description: "Summary tab content",
      })}
      tabIndex={0}
    >
      <InsightsSummary
        insightsSummary={application?.insightsSummary}
        isOpenToAllInternet={isOpenToAllInternet ?? undefined}
      />
      <AssetInformation assetInformation={assetInformation} />
      <Ownership ownership={ownership} assetId={assetId} variant={variant} />
      <Relationships relationships={relationships} />
      <AccessEntitlements accessEntitlements={accessEntitlements} />
    </div>
  );
}

export default memo(SummaryTab);
